/*
 * OrderVersionDashboard.tsx
 * Tableau de bord pour gérer une version de commande.
 * Permet l'affichage des informations principales de la commande (et un lien vers l'édition)
 * Ainsi que les informations spécifiques à la version de la commande (et un lien vers l'édition)
 * Et les lignes de commande
 * Et des actions rapides.
 */

// Imports React
import React, { JSX, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkflowAbortedError } from '../../../common/services/workflowClient';
import { Skeleton } from '@radix-ui/themes';
import * as Progress from '@radix-ui/react-progress';
import '@radix-ui/themes/styles.css';

// Imports FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faBriefcase,
	faCodeBranch,
	faAngleLeft,
	faPen,
	faCalendarAlt,
	faBox,
	faFileInvoiceDollar,
	faIndustry,
	faTrashAlt,
	faPlus,
	faFilePdf,
	faDownload,
	faFileCirclePlus,
	faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';

// Imports communs
import {
	SecondaryButton,
	navigateBack,
	DefinitionTermField,
	DefinitionDescriptionField,
	VersionCard,
	StatusCard,
} from '../../../common';
import { useWorkflowStatusConfig } from '../../../common/hooks/useWorkflowStatusConfig';

// Imports OrderVersion
import {
	ORDER_STATUS_CONFIG,
	ordersApi,
	OrderVersionData,
	orderVersionsApi,
} from '../';

// Imports Tiers, Address et Contact
import { AddressData, AddressTypeData } from 'features/addresses';
import { TiersData } from 'features/tiers';
import { orderLineApi } from '../services/orderLineApi';

// Composant OrderVersionDashboard
const OrderVersionDashboard: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = useState(true);
	const [formData, setFormData] = React.useState<OrderVersionData>();

	// Hooks de State (versions)
	const [versions, setVersions] = useState<OrderVersionData[]>([]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [isStable, setIsStable] = useState(false);

	// Hook de Navigation
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const statusConfig = useWorkflowStatusConfig(
		'order_version',
		ORDER_STATUS_CONFIG
	);

	// Labels pour les types de commandes
	const typeLabels: Record<string, string> = {
		UNIQUE: 'Unique',
		ANNUAL: 'Annuelle',
		REGULATION: 'Régulation',
	};

	// Fetch de la version de la commande et remplissage du formData
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;
			orderVersionsApi
				.get(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => setLoading(false));
		}
	}, [id]);

	React.useEffect(() => {
		if (formData) {
			const token = localStorage.getItem('token');
			if (!token) return;
			ordersApi
				.get(formData.businessOrder.id, token)
				.then((data) => setVersions(data.orderVersions ?? []))
				.catch((error) => console.error(error));
			setIsStable(true);
		}
	}, [formData]);

	// handle du changement de statut de la commande
	const handleStatusChange = (newStatus: string) => {
		if (formData) {
			const token = localStorage.getItem('token');
			if (!token) return;
			orderVersionsApi
				.update(formData.id, { status: newStatus }, token)
				.then((updated) => {
					setFormData((prev) =>
						prev
							? {
									...prev,
									status: updated?.status ?? newStatus,
									editedAt: updated?.editedAt ?? prev.editedAt,
									acknowledgedDate:
										updated?.acknowledgedDate ?? prev.acknowledgedDate,
									workflowLock: updated?.workflowLock ?? null,
								}
							: prev
					);
				})
				.catch((error) => {
					if (!(error instanceof WorkflowAbortedError)) console.error(error);
				});
		}
	};

	// Fonction pour obtenir la première adresse avec le code "LIV"
	const getFirstLivAddress = (tiers?: TiersData): JSX.Element | null => {
		const address = tiers?.addresses?.find((addr: AddressData) =>
			addr.types.some((type: AddressTypeData) => type.code === 'LIV')
		);
		if (!address) return null;
		return (
			<>
				{address.line1}
				<br />
				{address.line2 && (
					<>
						{address.line2}
						<br />
					</>
				)}
				{address.zipCode} {address.city}
				<br />
				{address.country}
			</>
		);
	};

	const formatDate = (iso: string) => {
		return new Date(iso).toLocaleDateString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	};

	const handleAddVersion = () => {
		setIsAddDialogOpen(false);

		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		orderVersionsApi
			.duplicate(formData.id, token)
			.then((newVersion) => {
				setVersions((prev) => [...prev, newVersion]);
				navigate(`/app/orderVersions/${newVersion.id}/manage`);
			})
			.catch(console.error);
	};

	const handleDeleteVersion = () => {
		setIsDeleteDialogOpen(false);

		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		orderVersionsApi
			.delete(formData.id, token)
			.then(() => {
				const remaining = versions.filter((v) => v.id !== formData.id);
				setVersions(remaining);

				if (remaining.length > 0) {
					const last = remaining[remaining.length - 1];
					navigate(`/app/orderVersions/${last.id}/manage`);
				} else {
					navigate('/app/orders');
				}
			})
			.catch(console.error);
	};

	const handleNavigateVersion = (id: number) => {
		navigate(`/app/orderVersions/${id}/manage`);
	};

	const handleDeleteOrderLine = (lineId: number) => {
		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		orderLineApi
			.delete(lineId, token)
			.then(() => {
				setFormData((prev) =>
					prev
						? {
								...prev,
								orderLines: prev.orderLines.filter(
									(line) => line.id !== lineId
								),
							}
						: prev
				);
			})
			.catch(console.error);
	};

	// Fonction pour télécharger le document PDF
	const handleDownloadPdf = () => {
		const token = localStorage.getItem('token');
		if (!token || !formData) return;

		orderVersionsApi
			.downloadPdf(formData.id, token)
			.then((blob) => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = `document-${formData.businessOrder.referenceNumber}.pdf`;
				document.body.appendChild(a);
				a.click();
				a.remove();
			})
			.catch(console.error);
	};

	// Fonction pour générer et ouvrir le PDF
	async function handleGenerateAndOpenPdf(orderVersionId?: number) {
		const token = localStorage.getItem('token');
		if (!token || !orderVersionId) return;

		// 1. Génération côté API
		const { documentId, path } = await orderVersionsApi.generatePdf(
			orderVersionId,
			token
		);

		// 2. Récupération du PDF
		const blob = await orderVersionsApi.downloadPdf(orderVersionId, token);
		const url = window.URL.createObjectURL(blob);
		window.open(url, '_blank');

		// 3. Mise à jour du state pour rafraîchir la card
		setFormData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				document: {
					id: documentId,
					path: path,
				},
			};
		});
	}

	// Fonction pour supprimer le document PDF
	const handleDeletePdf = async (orderVersionId?: number) => {
		const token = localStorage.getItem('token');
		if (!token || !orderVersionId) return;

		try {
			await orderVersionsApi.deletePdf(orderVersionId, token);
			// If we just deleted the PDF for the currently displayed version, clear it from state
			if (formData && formData.id === orderVersionId) {
				setFormData((prev) => {
					if (!prev) return prev; // si prev undefined, on ne change rien
					return { ...prev, document: null }; // prev est bien OrderVersionData
				});
			}
		} catch (error) {
			console.error(error);
			alert('Impossible de supprimer le PDF');
		}
	};

	// Rendu du composant
	return (
		<div>
			{/* Grille haute : 3 colonnes (blocs Dates, versions et statut) */}
			<div className="grid gap-6 grid-cols-1 md:grid-cols-3 mb-6">
				{/* CARD Contenant les informations des dates (date de création, date de début et de fin) */}
				<div className=" p-6 bg-white rounded-lg shadow-md">
					<h2 className="text-2xl font-bold mb-4">
						<FontAwesomeIcon
							icon={faCalendarAlt}
							className="w-6 h-6 text-gray-700"
						/>{' '}
						Dates :
					</h2>
					<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
						{/* Date de création de la version de commande */}
						<DefinitionTermField
							label="Version créée le :"
							tooltip="Date de création de la version de la commande"
						/>
						<DefinitionDescriptionField
							value={
								new Date(formData?.createdAt ?? '').toLocaleDateString(
									'fr-FR'
								) || '—'
							}
							loading={loading}
						/>

						{formData?.editedAt && (
							<>
								<DefinitionTermField
									label="Éditée le :"
									tooltip="Date d'édition intégrée au PDF de la commande"
								/>
								<DefinitionDescriptionField
									value={new Date(formData.editedAt).toLocaleDateString(
										'fr-FR'
									)}
									loading={loading}
								/>
							</>
						)}

						{/* Date de reception d'AR si présente */}
						{formData?.acknowledgedDate && (
							<>
								<DefinitionTermField
									label="Réception AR :"
									tooltip="Date à laquelle l'accusé de réception a été reçu"
								/>
								<DefinitionDescriptionField
									value={
										new Date(
											formData?.acknowledgedDate ?? ''
										).toLocaleDateString('fr-FR') || '—'
									}
									loading={loading}
								/>
							</>
						)}

						{/* Date attendue pour la livraison de la commande */}
						<DefinitionTermField
							label="Prévue pour :"
							tooltip="Date à laquelle la commande est prévue pour être livrée"
						/>
						<DefinitionDescriptionField
							value={
								new Date(formData?.deliveryDate ?? '').toLocaleDateString(
									'fr-FR'
								) || '—'
							}
							loading={loading}
						/>

						{/* Date de reception si présente */}
						{formData?.deliveredAt && (
							<>
								<DefinitionTermField
									label="Réception effective :"
									tooltip="Date à laquelle la commande a été livrée"
								/>
								<DefinitionDescriptionField
									value={
										new Date(formData?.deliveredAt ?? '').toLocaleDateString(
											'fr-FR'
										) || '—'
									}
									loading={loading}
								/>
							</>
						)}
					</dl>
				</div>

				{/* CARD Contenant les informations des versions de devis */}
				<VersionCard
					icon={faCodeBranch}
					title="Versions"
					formData={formData}
					versions={versions}
					loading={loading}
					isStable={isStable}
					onNavigate={handleNavigateVersion}
					onAdd={handleAddVersion}
					onDelete={handleDeleteVersion}
					isDeleteDialogOpen={isDeleteDialogOpen}
					setIsDeleteDialogOpen={setIsDeleteDialogOpen}
					isAddDialogOpen={isAddDialogOpen}
					setIsAddDialogOpen={setIsAddDialogOpen}
				/>

				{/* CARD Contenant le statut de la commande */}
				<StatusCard
					status={formData?.status}
					config={statusConfig}
					onStatusChange={handleStatusChange}
					loading={loading}
				/>

				{/* Deuxième ligne : 2 colonnes (informations principales à gauche, affaires et autres versions à droite) */}

				{/* CARDS Informations principales */}
				<div className="col-span-2 p-6 bg-white rounded-lg shadow-md">
					{/* Ligne d'entête */}
					<div className="flex items-center mb-4">
						{/* Avatar rond avec icon faBox*/}
						{/* Test si le type de tiers est CLI */}

						<div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mr-4">
							<FontAwesomeIcon icon={faBox} className="w-7 h-7 text-gray-50" />
						</div>

						{/* Sinon, Placeholder pendant le chargement */}
						<h1 className="text-3xl font-bold mr-4">
							{loading ? (
								<Skeleton
									className="w-80 h-10 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
									style={{
										backgroundSize: '200% 100%',
										animation: 'shimmer 1.5s infinite linear',
									}}
								/>
							) : (
								'Commande ' + formData?.businessOrder.referenceNumber
							)}
						</h1>
					</div>

					{/* Informations de la commande */}
					<div className="pt-4 pb-2">
						<p>
							<b>Informations principales : </b>
							<FontAwesomeIcon
								icon={faPen}
								className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-gray-600"
								onClick={() => {
									navigate(`/app/orders/${formData?.businessOrder.id}/edit`);
								}}
							/>
							<div className="grid grid-cols-2 gap-2">
								<div>
									<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
										{/* Référence : numéro de référence interne de la commande (ex : 001/25-A) */}
										<DefinitionTermField
											label="Nos ref. :"
											tooltip="Numéro interne de référence de la commande"
										/>
										<DefinitionDescriptionField
											value={formData?.businessOrder.referenceNumber || '—'}
											loading={loading}
										/>
									</dl>
								</div>
								<div>
									<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
										{/* Référence : numéro de référence interne de la commande (ex : 001/25-A) */}
										<DefinitionTermField
											label="Classification :"
											tooltip="Classification de la commande (Ex : Unique, Annuelle, Régulation)"
										/>
										<DefinitionDescriptionField
											value={
												typeLabels[formData?.businessOrder.type || ''] || '—'
											}
											loading={loading}
										/>
									</dl>
								</div>
							</div>
						</p>
					</div>

					{/* Informations de la version de la commande */}
					<div className="pt-4 pb-2">
						<p>
							<b>Informations de version : </b>
							{/* Icon crayon permettant de naviguer vers /app/orderVersions/${formData?.id}/edit */}
							<FontAwesomeIcon
								icon={faPen}
								className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-gray-600"
								onClick={() => {
									navigate(`/app/orderVersions/${formData?.id}/edit`);
								}}
							/>
						</p>
						<div className="grid grid-cols-2 gap-2">
							{/* Labels à gauche, données à droite, les colonnes sont de largeur minimale */}
							<div>
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
									{/* Exercice : pour l'analyse des Résultats (pas du point de vue comptabilité) */}
									<DefinitionTermField
										label="Exercice :"
										tooltip="Pour l'analyse des Résultats (pas du point de vue comptabilité)"
									/>
									<DefinitionDescriptionField
										value={formData?.fiscalYear || '—'}
										loading={loading}
									/>

									{/* Votre référence : Référence du fournisseur pour cette commande */}
									<DefinitionTermField
										label="Vos ref. :"
										tooltip="Référence du fournisseur pour cette commande"
									/>
									<DefinitionDescriptionField
										value={formData?.tiersReference || '—'}
										loading={loading}
									/>

									{/* Pilote : contact interne en charge de la commande */}
									<DefinitionTermField
										label="Pilote :"
										tooltip="Contact interne en charge de la commande"
									/>
									<DefinitionDescriptionField
										value={
											formData?.assignedUser?.name
												? formData?.assignedUser?.name +
													' ' +
													formData?.assignedUser?.firstName
												: '—'
										}
										loading={loading}
									/>
								</dl>
							</div>

							<div>
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
									{/* Signataire : nom du ou des signataire(s) interne(s) de la commande */}
									<DefinitionTermField
										label="Signataire :"
										tooltip="Nom du ou des signataire(s) interne(s) de la commande"
									/>
									<DefinitionDescriptionField
										value={formData?.signatory || '—'}
										loading={loading}
									/>

									{/* Référence : référence supplémentaire pour la commande du type "Prénom NOM - TRI" */}
									<DefinitionTermField
										label="Nos ref. :"
										tooltip="Référence supplémentaire pour la commande du type 'Prénom NOM - TRI'"
									/>
									<DefinitionDescriptionField
										value={formData?.generatedText || '—'}
										loading={loading}
									/>

									{/* Conditions particulières : texte libre pour ajouter des informations qui apparaitront en bas de page sur la commande */}
									<DefinitionTermField
										label="Observations :"
										tooltip="Texte libre pour ajouter des informations qui apparaitront en bas de page sur la commande"
									/>
									<DefinitionDescriptionField
										value={formData?.publicNotes || '—'}
										loading={loading}
									/>
								</dl>
							</div>
						</div>
					</div>
					<div className="pt-4 pb-2">
						<p>
							<b>Lignes de la commande</b>
							<button
								className="text-gray-400 hover:text-green-800"
								onClick={() => {
									navigate(
										`/app/orderLines/new?orderVersionId=${formData?.id}`
									);
								}}
							>
								<FontAwesomeIcon icon={faPlus} className="ml-2" />
							</button>
							{/* Tableau des lignes de la commande */}
							<div className="overflow-x-auto mt-2">
								<table className="min-w-full border border-gray-200 rounded-lg text-sm">
									<thead className="bg-gray-100">
										<tr>
											<th className="px-3 py-2 border-b text-left">Label</th>
											<th className="px-3 py-2 border-b text-right">
												Date livr.
											</th>
											<th className="px-3 py-2 border-b text-right">Code</th>
											<th className="px-3 py-2 border-b text-right">Qté</th>
											<th className="px-3 py-2 border-b text-right">PU</th>
											<th className="px-3 py-2 border-b text-right">
												Total HT
											</th>
											<th className="px-3 py-2 border-b text-right">Taxe</th>
											<th className="px-3 py-2 border-b text-right">
												Total TTC
											</th>
											<th className="px-3 py-2 border-b text-right">Actions</th>
										</tr>
									</thead>
									<tbody>
										{loading ? (
											// Skeleton quand cela charge
											[1, 2, 3].map((i) => (
												<tr key={i} className="animate-pulse">
													<td className="px-3 py-2 border-b">
														<div className="h-4 bg-gray-200 rounded w-40"></div>
													</td>
													<td className="px-3 py-2 border-b text-right">
														<div className="h-4 bg-gray-200 rounded w-10 ml-auto"></div>
													</td>
													<td className="px-3 py-2 border-b text-right">
														<div className="h-4 bg-gray-200 rounded w-8 ml-auto"></div>
													</td>
													<td className="px-3 py-2 border-b text-right">
														<div className="h-4 bg-gray-200 rounded w-12 ml-auto"></div>
													</td>
													<td className="px-3 py-2 border-b"></td>
													<td className="px-3 py-2 border-b"></td>
													<td className="px-3 py-2 border-b"></td>
													<td className="px-3 py-2 border-b"></td>
												</tr>
											))
										) : formData?.orderLines &&
										  formData.orderLines.length > 0 ? (
											<>
												{formData.orderLines.map((line) => (
													<tr key={line.id}>
														<td className="px-3 py-2 border-b">{line.label}</td>
														<td className="px-3 py-2 border-b text-right">
															<div className="flex flex-col gap-4">
																{formatDate(line.deliveryDate ?? '—')}
																<Progress.Root className="relative w-full h-2 bg-gray-200 rounded-full">
																	<Progress.Indicator
																		className={`h-full ${(line.deliveredPercent ?? 0) >= 100 ? 'bg-green-600' : 'bg-blue-600'} rounded-full`}
																		style={{
																			width: `${line.deliveredPercent ?? 0}%`,
																			transform: 'none',
																		}}
																	/>
																</Progress.Root>
															</div>
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.accountingCode ? line.accountingCode : '—'}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.quantity}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.unitPrice?.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.totalExclTax?.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
														</td>
														<td className="px-3 py-2 border-b text-right">
															{line.taxRate?.rate
																? (Number(line.taxRate.rate) * 100).toFixed(2) +
																	'%'
																: '—'}
														</td>
														<td className="px-3 py-2 border-b text-right font-semibold">
															{(line.totalExclTax
																? line.totalExclTax *
																	(1 + Number(line.taxRate?.rate ?? 0))
																: 0
															).toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
														</td>
														<td className="px-3 py-2 border-b text-right">
															<FontAwesomeIcon
																icon={faPen}
																className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-blue-600"
																onClick={() => {
																	navigate(`/app/orderLines/${line.id}/edit`);
																}}
															/>
															<FontAwesomeIcon
																icon={faTrashAlt}
																className="w-4 h-4 text-gray-400 opacity-80 ml-2 cursor-pointer hover:text-red-600"
																onClick={() => {
																	handleDeleteOrderLine(line.id);
																}}
															/>
														</td>
													</tr>
												))}
												<tr className="bg-gray-100">
													<td
														className="px-3 py-3 border-b text-left font-bold"
														colSpan={5}
													>
														Total
													</td>
													<td
														className="px-3 py-3 border-b text-right font-bold"
														colSpan={1}
													>
														{formData.orderLines
															.reduce(
																(acc, line) => acc + (line.totalExclTax ?? 0),
																0
															)
															.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
													</td>
													<td className="px-3 py-3 border-b"></td>
													<td
														className="px-3 py-3 border-b text-right font-bold"
														colSpan={1}
													>
														{formData.orderLines
															.reduce((acc, line) => {
																const lineTotalTTC =
																	(line.totalExclTax ?? 0) *
																	(1 + Number(line.taxRate?.rate ?? 0));
																return acc + lineTotalTTC;
															}, 0)
															.toLocaleString('fr-FR', {
																minimumFractionDigits: 2,
															})}
													</td>
													<td className="px-3 py-3 border-b"></td>
												</tr>
											</>
										) : (
											<tr>
												<td
													className="px-3 py-3 border-b text-center text-gray-500"
													colSpan={6}
												>
													Aucune ligne dans cete commande.
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</p>
					</div>

					<div className="flex justify-end mt-4 space-x-2">
						<SecondaryButton
							onClick={() =>
								navigateBack(navigate, () => navigate('/app/orders'))
							}
							label="Retour"
							size="lg"
							icon={faAngleLeft}
						/>
					</div>
				</div>

				<div className="col-span-1 space-y-4">
					{/* CARD AFFAIRE alignement vertical */}
					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faBriefcase}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Affaire{(formData?.linkedProjects?.length ?? 0) > 1 ? 's' : ''} :
						</h2>
						{loading ? (
							// Skeleton pendant le chargement
							<div className="space-y-2">
								{[1, 2].map((i) => (
									<div
										key={i}
										className="h-4 bg-gray-200 rounded w-32 animate-pulse"
									/>
								))}
							</div>
						) : formData?.linkedProjects &&
						  formData.linkedProjects.length > 0 ? (
							<ul className="space-y-1">
								{formData.linkedProjects.map((project) => (
									<li key={project.id}>
										<a
											href={`/app/projects/${project.id}/edit`}
											className="text-blue-600 hover:underline font-medium"
										>
											{project.referenceNumber}
										</a>
									</li>
								))}
							</ul>
						) : (
							<p className="text-gray-500 text-sm">Aucune affaire liée</p>
						)}
					</div>

					{/* CARD FACTURES FOURNISSEUR */}
					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faFileInvoiceDollar}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Facture
							{(formData?.linkedSupplierInvoices?.length ?? 0) > 1
								? 's'
								: ''}{' '}
							fournisseur :
						</h2>
						{loading ? (
							// Skeleton pendant le chargement
							<div className="space-y-2">
								{[1, 2].map((i) => (
									<div
										key={i}
										className="h-4 bg-gray-200 rounded w-32 animate-pulse"
									/>
								))}
							</div>
						) : formData?.linkedSupplierInvoices &&
						  formData.linkedSupplierInvoices.length > 0 ? (
							<ul className="space-y-1">
								{formData.linkedSupplierInvoices.map((invoice) => (
									<li key={invoice.id}>
										<a
											href={`/app/supplierInvoices/${invoice.id}/edit`}
											className="text-blue-600 hover:underline font-medium"
										>
											{invoice.referenceNumber}
										</a>
									</li>
								))}
							</ul>
						) : (
							<p className="text-gray-500 text-sm">
								Aucune facture fournisseur liée
							</p>
						)}
					</div>

					{/* CARD FOURNISSEUR */}
					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faIndustry}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Fournisseur :
						</h2>
						<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
							<DefinitionTermField label="Nom :" />
							<a
								href={`/app/tiers/${formData?.businessOrder.tiers?.id}/manage`}
							>
								<DefinitionDescriptionField
									value={formData?.businessOrder.tiers?.label || '—'}
									loading={loading}
								/>
							</a>
							<DefinitionTermField label="Adresse :" />
							<dd>
								{getFirstLivAddress(
									formData?.businessOrder.tiers ?? undefined
								)}{' '}
							</dd>
							<DefinitionTermField label="Contact :" />
							<dd>
								{(() => {
									const contacts =
										formData?.businessOrder.tiers?.contacts ?? [];
									const contact =
										contacts.find((c) => c.businessManager && c.status) ??
										contacts.find((c) => c.status);
									if (!contact) return '—';
									const name = [contact.firstName, contact.lastName]
										.filter(Boolean)
										.join(' ');
									return contact.email ? (
										<>
											{name}
											<br />
											<a
												href={`mailto:${contact.email}`}
												className="text-blue-600 hover:underline"
											>
												{contact.email}
											</a>
										</>
									) : (
										name || '—'
									);
								})()}
							</dd>
						</dl>
					</div>

					{/* CARD DOCUMENT */}
					<div className="mx-auto p-6 bg-white rounded-lg shadow-md">
						<h2 className="text-2xl font-bold mb-4">
							<FontAwesomeIcon
								icon={faFilePdf}
								className="w-6 h-6 text-gray-700"
							/>{' '}
							Document
						</h2>
						{formData?.document ? (
							<>
								{formData.document.path.split('\\').pop()}
								<button
									className="ml-2 text-gray-400 hover:text-blue-800"
									onClick={handleDownloadPdf}
								>
									<FontAwesomeIcon icon={faDownload} />
								</button>
								<button
									className="ml-2 text-gray-400 hover:text-blue-800"
									onClick={() => {
										handleGenerateAndOpenPdf(formData?.id).catch(console.error);
									}}
								>
									<FontAwesomeIcon icon={faArrowsRotate} />
								</button>
								<button
									className="ml-2 text-gray-400 hover:text-red-800"
									onClick={() => handleDeletePdf(formData?.id)}
								>
									<FontAwesomeIcon icon={faTrashAlt} />
								</button>
							</>
						) : (
							<>
								<span className="text-gray-500">Aucun document généré</span>
								<button
									className="ml-2 text-gray-400 hover:text-green-800"
									onClick={() => {
										handleGenerateAndOpenPdf(formData?.id).catch(console.error);
									}}
								>
									<FontAwesomeIcon icon={faFileCirclePlus} />
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
export default OrderVersionDashboard;
